import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import packageJson from '../../package.json';
import { usePermissions } from '../context/PermissionsContext';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EmailIcon from '@mui/icons-material/Email';
import PeopleIcon from '@mui/icons-material/People';
import CloudIcon from '@mui/icons-material/Cloud';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ShieldIcon from '@mui/icons-material/Shield';
import StorageIcon from '@mui/icons-material/Storage';
import DescriptionIcon from '@mui/icons-material/Description';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import MemoryIcon from '@mui/icons-material/Memory';
import DnsIcon from '@mui/icons-material/Dns';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import TuneIcon from '@mui/icons-material/Tune';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import ListAltIcon from '@mui/icons-material/ListAlt';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LogoutIcon from '@mui/icons-material/Logout';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LoginIcon from '@mui/icons-material/Login';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';

const drawerWidth = 240;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', adminOnly: false },
  { text: 'Patches', icon: <NewReleasesIcon />, path: '/patches', adminOnly: false },
  { text: 'Customers', icon: <PeopleIcon />, path: '/customers', adminOnly: false },
  { text: 'Environments', icon: <CloudIcon />, path: '/environments', adminOnly: false },
  { text: 'Scheduled Jobs', icon: <ScheduleIcon />, path: '/scheduled-jobs', adminOnly: true },
  { text: 'Email Logs', icon: <EmailIcon />, path: '/email-logs', adminOnly: true },
  { text: 'System Requests', icon: <AssignmentIcon />, path: '/system-requests', adminOnly: false },
  { text: 'Customer Licenses', icon: <VpnKeyIcon />, path: '/licenses', adminOnly: true },
  { text: 'Knowledge Base', icon: <MenuBookIcon />, path: '/kb', adminOnly: false },
  { text: 'Backup Reports', icon: <StorageIcon />, path: '/backups', adminOnly: false, needsBackups: true },
  { text: 'Failed Logins', icon: <GppMaybeIcon />, path: '/failed-logins', adminOnly: false, needsBackups: true },
  { text: 'WAF Monitor', icon: <ShieldIcon />, path: '/waf', adminOnly: true },
  { text: 'Login History', icon: <LoginIcon />, path: '/login-history', adminOnly: true },
  { text: 'Customer Logs', icon: <DescriptionIcon />, path: '/customer-logs', adminOnly: false },
  { text: 'Licensing Whitelist', icon: <PlaylistAddCheckIcon />, path: '/licensing-whitelist', adminOnly: false },
  { text: 'Customer Uptime', icon: <MonitorHeartIcon />, path: '/uptime', adminOnly: false },
  { text: 'Server Resources', icon: <MemoryIcon />, path: '/server-resources', adminOnly: false },
  { text: 'Database Servers', icon: <DnsIcon />, path: '/database-servers', adminOnly: false },
  { text: 'Telegraf Fleet', icon: <TuneIcon />, path: '/telegraf-fleet', adminOnly: true },
  { text: 'Monitor Servers', icon: <SettingsInputAntennaIcon />, path: '/monitor-servers', adminOnly: true },
  { text: 'Service Actions', icon: <ManageHistoryIcon />, path: '/service-actions', adminOnly: true },
  { text: 'SFTP Access', icon: <SwapHorizIcon />, path: '/sftp-access', adminOnly: false },
  { text: 'Customer Log Sources', icon: <FolderSpecialIcon />, path: '/log-sources', adminOnly: true },
  { text: 'Application Logs', icon: <ListAltIcon />, path: '/app-logs', adminOnly: true },
  { text: 'Users', icon: <AdminPanelSettingsIcon />, path: '/users', adminOnly: true },
];

function Sidebar({ userEmail, authType }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const { isAdmin, canReviewBackups, loading } = usePermissions();

  const handleLogout = async () => {
    if (authType === 'entra') {
      // SSO logout
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } else {
      // OTP logout
      localStorage.removeItem('otp_token');
      window.location.reload();
    }
  };

  const visibleMenuItems = menuItems.filter((item) => {
    if (item.needsBackups) return isAdmin() || canReviewBackups();
    return !item.adminOnly || isAdmin();
  });

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
    >
      <Toolbar variant="dense">
        <Typography variant="subtitle1" noWrap component="div" sx={{ fontWeight: 500 }}>
          Management Portal
        </Typography>
      </Toolbar>
      <Divider />
      <List dense disablePadding sx={{ py: 0.5 }}>
        {visibleMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{ py: 0, minHeight: 32 }}
            >
              <ListItemIcon sx={{ minWidth: 32, '& .MuiSvgIcon-root': { fontSize: 18 } }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} sx={{ my: 0.25, '& .MuiListItemText-primary': { fontSize: '0.84rem' } }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <Box sx={{ px: 2, py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
            {userEmail}
          </Typography>
          <Tooltip title="Logout">
            <IconButton size="small" onClick={handleLogout}>
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box
          onClick={() => navigate('/changelog')}
          sx={{
            cursor: 'pointer',
            textAlign: 'center',
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          <Typography variant="caption" display="block" color="primary">
            v{packageJson.version} - View Changelog
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
}

export default Sidebar;
